import {
  useQuery,
  DocumentNode,
  QueryHookOptions,
  ApolloError,
  useLazyQuery,
  QueryResult,
  OperationVariables,
  from,
  ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getClient } from '~/apollo/client';

// TODO: really, these should come from feed-domain.
export interface OperationLog {
  name: string;
  events: OperationLogEvent[];
  extra?: Record<string, any>;
}

export interface OperationLogEvent {
  message: string;
  type: string;
  elapsedTime: number;
  extra?: Record<string, any>;
}

/**
 * Link: enables debug mode at the server and captures the operation log to result.data or
 * result.errors. This is tightly coupled with `addOperationLogToResult`.
 *
 * Explanation: Apollo gives us an `extensions` field at the top-level of the response, with which
 * to added non-GraphQL data. We use this to carry the operation log. However, `useQuery` does not
 * expose this in any way. To support being able to wrap `useQuery` and extract the operation log
 * from it, we have to add the operation log to what `useQuery` gives us: `data` or `errors`. Both
 * options are a little funny.
 *
 * The implementation below uses whichever of those is defined in the response. Regardless which
 * property is used, it is removed on the other side (see useQueryWithDebug).
 *
 * A better way would be nice...
 */
export const debugLink = from( [
  // Add header to trigger debug output from server.
  setContext( ( _operation, { headers } ) => ( {
    headers: { ...headers, 'X-FeedGraphQL-EnableDebug': 'true' },
  } ) ),

  // Extract debug output into a location where useQuery can find it.
  new ApolloLink( ( operation, forward ) => {
    return forward( operation ).map( ( result ) => {
      // Add log to either data or errors, whichever is populated.
      if ( result.data ) {
        result.data.__operationLog = result.extensions?.operationLog;
      } else if ( result.errors ) {
        ( result.errors as any ).__operationLog = result.extensions?.operationLog;
      }

      return result;
    } );
  } ),
] );

export type WithOperationLog<TData> = TData & { __operationLog: OperationLog };

/**
 * Given a query result of type TData, return the same result but with `__operationLog` (from
 * `debugLink` above) added as a top-level key. `operationLog` may be undefined.
 *
 * This is tightly coupled with `debugLink`.
 */
export function addOperationLogToResult<TData, TVariables extends OperationVariables>(
  result: QueryResult<TData, TVariables>,
): QueryResult<TData, TVariables> & { operationLog?: OperationLog } {
  if ( result.data ) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { __operationLog, ...originalData } = result.data as WithOperationLog<TData>;
    return {
      ...result,
      data: originalData as unknown as TData,
      operationLog: __operationLog,
    };
  }

  if ( result.error ) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { __operationLog, ...originalError } = result.error as WithOperationLog<ApolloError>;
    return {
      ...result,
      error: originalError,
      operationLog: __operationLog,
    };
  }

  return result;
}

/**
 * Wrapper around useQuery that will add an `operationLog` parameter to the result.
 */
export const useQueryWithDebug = <TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {},
) => {
  const client = getClient( true );
  const result = useQuery<TData, TVariables>( query, { ...options, client } );
  return addOperationLogToResult( result );
};

/**
 * Wrapper around useQueryWithDebuggLazy that will add an `operationLog` parameter to the result.
 */
export const useQueryWithDebugLazy = <TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {},
) => {
  const client = getClient( true );
  const [executeQuery, result] = useLazyQuery<TData, TVariables>( query, { ...options, client } );
  return [executeQuery, addOperationLogToResult( result )];
};
